// Libs
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';

// Context
import { AdminBinderContext } from '../AdminBinder/AdminLayout';
import { useContext } from 'react';

// Components
import { EditBinderTemplate } from '@ibinder/common-frontend';

// Labels
import { binderTemplateLabels } from './binderTemplates.labels';

// Utils
import { useStateHook } from '@ibinder/common-utils';
import { cloneDeep } from '@apollo/client/utilities';
import { v4 as uuid } from 'uuid';

// Consts
import { ApplicationTabs, TabGroup } from 'Shared/Utils/constants/commonConsts';
import {
  BinderTemplateStates,
  EMPTY_TEMPLATE,
  Tab,
  BinderTemplate
} from 'Shared/Utils/constants/commonConsts';

// Icons
import { edit } from '@ibinder/common-icons';

// Queries
import {
  GET_ROLETYPES_QUERY,
  GET_TAB_TEMPLATES,
  GET_TEMPLATE,
  GET_USERS,
  PUT_TEMPLATE_MUTATION
} from '../../api/queries/queries_local';
import { accessCheck } from 'Shared/api/queries/useAccessQuery';

// Helpers
import {
  setPropsOnTemplateTabsRecursively,
  setStatusSavedRecursive
} from './Helper/tabHelper';
import { TabTypes } from '@ibinder/common-utils';
import { useAllRolesForAllTypes } from './useAllRolesForAllTypes';
import { iBinderHeaderEnum } from '@ibinder/ui-common-models';

const nameSortComparer = (a, b) => {
  if (a.originalName < b.originalName) {
    return -1;
  }
  if (a.originalName > b.originalName) {
    return 1;
  }
  return 0;
};

const EditBinderTemplates = () => {
  //Params
  const { templateId } = useParams();
  const userId = localStorage.getItem('userId');
  const client = useApolloClient();
  const navigate = useNavigate();

  // Context
  const { rightPanelOpen, toggleTabsMenu } = useContext(AdminBinderContext);

  const [roleTypes, setRoleTypes] = React.useState([]);
  const [currentTabsPath, setCurrentTabsPath] = React.useState([]);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [tabTypes, setTabTypes] = React.useState([]);
  const [templateData, setTemplateData] =
    React.useState<BinderTemplate>(EMPTY_TEMPLATE);
  const [availableTabTemplates, setAvailableTabTemplates] = React.useState<
    Tab[]
  >([]);
  const [unSavedChanges, setUnsavedChanges] = React.useState<boolean>(false);

  const [isAction, setAction] = useStateHook<BinderTemplateStates>({
    state: BinderTemplateStates.VIEW_TEMPLATE
  });

  const templateActions: { icon: any; action: BinderTemplateStates }[] = [
    { icon: edit, action: BinderTemplateStates.EDIT_ABOUT_TEMPLATE },
    { icon: edit, action: BinderTemplateStates.EDIT_USERS_AND_ROLES },
    { icon: edit, action: BinderTemplateStates.EDIT_TABS }
  ];

  const useTemplateQuery = (templateId: string, userId: string) =>
    accessCheck(
      useQuery(GET_TEMPLATE, {
        context: {
          headers: { [iBinderHeaderEnum.TENANTID]: '' } // Needs to be empty string to override automatic set tenantId
        },
        variables: { templateId },
        notifyOnNetworkStatusChange: true
      })
    );

  // Get template
  const {
    loading: templatesLoading,
    data: templatesData,
    refetch: refetchTemplates,
    networkStatus: networkStatusTemplate
  } = useTemplateQuery(templateId, userId);

  // Get roletypes
  const AllRoleTypes = accessCheck(
    useQuery(GET_ROLETYPES_QUERY, {
      context: {
        headers: { [iBinderHeaderEnum.TENANTID]: '' } // Needs to be empty string to override automatic set tenantId
      }
    })
  );

  // Get Roles
  const [roles] = useAllRolesForAllTypes(
    roleTypes,
    client,
    binderTemplateLabels()
  );

  // Get users
  const participantsListData = accessCheck(
    useQuery(GET_USERS, {
      variables: {
        filterQuery: searchQuery
      }
    })
  );

  // // Put global binder template
  const useEditTemplateQuery = (onCompleted?) =>
    useMutation(PUT_TEMPLATE_MUTATION, {
      context: {
        headers: { [iBinderHeaderEnum.TENANTID]: '' } // Needs to be empty string to override automatic set tenantId
      },
      variables: {
        templateId: templatesData?.template?.id
      },
      onCompleted
    });

  // Get binder template tabs
  const useTabTemplatesQuery = () =>
    accessCheck(
      useQuery(GET_TAB_TEMPLATES, {
        context: {
          headers: { [iBinderHeaderEnum.TENANTID]: '' } // Needs to be empty string to override automatic set tenantId
        },
        variables: {
          templateId
        }
      })
    );

  const { loading: tabTemplatesLoading, data: tabTemplatesData } =
    useTabTemplatesQuery();

  const onEditCompleted = (d) => {
    const tempTemplateData = cloneDeep(templateData);
    tempTemplateData.tabs = setStatusSavedRecursive(tempTemplateData.tabs);
    setTemplateData(tempTemplateData);
    setUnsavedChanges(false);
  };

  const [editTemplate] = useEditTemplateQuery(onEditCompleted);
  const [editTemplateWOonComplete] = useEditTemplateQuery();
  const onSelect = (action) => setAction(action);
  const PARTICIPANTS_DATA = participantsListData.data?.get.data;

  const goBackToView = () => {
    let Abort = unSavedChanges
      ? confirm('You have not saved your changes')
      : !unSavedChanges;

    if (Abort) {
      setCurrentTabsPath([]);
      refetchTemplates();
      setUnsavedChanges(false);
      setAction(BinderTemplateStates.VIEW_TEMPLATE);
    }
  };

  React.useEffect(() => {
    if (networkStatusTemplate === 7 && !tabTemplatesLoading) {
      const templates = templatesData?.template
        ? {
            ...templatesData?.template,
            tabs: setPropsOnTemplateTabsRecursively(
              templatesData?.template?.tabs?.map((t, key) => {
                const foundTemplate = tabTemplatesData.tabTemplates.find(
                  (tt) => tt.id === t.tabTemplateId
                );
                return {
                  ...t,
                  id: key,
                  inLineTabTemplate: foundTemplate
                    ? { ...foundTemplate, id: uuid() } //To avoid having the same id on the inlinetabtemplate as the real template, just causes confusion.
                    : t.inLineTabTemplate
                };
              })
            )
          }
        : undefined;
      setTemplateData(cloneDeep(templates) ?? EMPTY_TEMPLATE);
    }
  }, [templatesLoading, networkStatusTemplate, tabTemplatesLoading]);

  React.useEffect(() => {
    if (!tabTemplatesLoading) {
      setAvailableTabTemplates(
        tabTemplatesData.tabTemplates.map((tt) => ({
          ...tt,
          name: tt.name,
          originalName: tt.name,
          expanded: true
        }))
      );
    }
  }, [tabTemplatesLoading, tabTemplatesData]);

  React.useEffect(() => {
    setTabTypes(
      availableTabTemplates.sort(nameSortComparer).map((tt) => ({
        Key: tt.id,
        Value: tt.name,
        group: ApplicationTabs.find((t) => t === tt.type)
          ? TabGroup.Application
          : tt.type === TabTypes.Navigation
          ? TabGroup.Structure
          : TabGroup.List,
        type: tt.type
      }))
    );
  }, [availableTabTemplates]);

  React.useEffect(() => {
    if (!AllRoleTypes.loading) {
      setRoleTypes(AllRoleTypes.data.get);
    }
  }, [AllRoleTypes]);

  const onClickGoToPath = (home: string) => {
    refetchTemplates();
    navigate(home);
  };

  const chipsData = [
    {
      name: 'Superadmin',
      id: 'admin',
      onClick: () => onClickGoToPath('/')
    },
    {
      name: 'Global binder templates',
      id: 'global',
      onClick: () => onClickGoToPath('/tab/13')
    },
    {
      name: templatesData?.template?.name
    }
  ];

  return (
    <>
      <EditBinderTemplate
        {...{
          binderInformation: undefined,
          labels: binderTemplateLabels(),
          chipsData,
          translatedChipsData: chipsData,
          translatedName: chipsData[chipsData.length - 1].name,
          roles,
          binderId: undefined,
          tenantId: undefined,
          tabTypes,
          templateData,
          setTemplateData,
          tabTemplatesData,
          availableTabTemplates,
          isAction,
          setAction,
          templateActions,
          editTemplate,
          editTemplateWOonComplete,
          refetchTemplates,
          goBackToView,
          templatesLoading: false,
          unSavedChanges,
          setUnsavedChanges,
          logos: undefined,
          setLogos: undefined,
          onSelect,
          isBinderPanelOpen: rightPanelOpen,
          toggleTabs: () => toggleTabsMenu(),
          searchQuery,
          setSearchQuery,
          UploadLogo: undefined,
          // We will only need SelectExistingTemplate
          // when we can create binder templates from Admin
          SelectExistingTemplate: <></>,
          canInheritTemplate: false,
          currentTabsPath,
          setCurrentTabsPath
        }}
        participantsListData={PARTICIPANTS_DATA}
      />
    </>
  );
};

export default EditBinderTemplates;
